import type { InventoryUploadResponse } from 'contracts/inventory-2/uploads';
import { Box } from 'components/ui/Box';
import {
  CLASSIFICATION_LABEL,
  INVENTORY_UPLOAD_MODE_LABEL,
  INVENTORY_UPLOAD_TYPE_OPTIONS,
} from 'constants/inventory-2/uploads';

type Props = {
  result: InventoryUploadResponse;
};

const formatSheetField = (value: number | string[] | undefined) => {
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.length ? `${value.length} (${value.join(', ')})` : '0';
  }
  return null;
};

const CountList = ({
  title,
  counts,
  labelFor,
}: {
  title: string;
  counts?: Record<string, number>;
  labelFor?: (key: string) => string;
}) => {
  if (!counts || Object.keys(counts).length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="font-GilroySemibold text-gray-600">{title}</p>
      <ul className="text-sm text-slate-600">
        {Object.entries(counts).map(([key, value]) => (
          <li key={key}>
            {labelFor ? labelFor(key) : key}: {value}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const UploadResult = ({ result }: Props) => {
  const modeLabel = INVENTORY_UPLOAD_MODE_LABEL[result.mode] || result.mode;
  const cycleSheets = formatSheetField(result.cycleSheets);
  const ignoredSheets = formatSheetField(result.ignoredSheets);
  const rows: Array<{ label: string; value: string | number }> = [
    { label: 'Arquivo', value: result.file?.name },
    { label: 'Modo', value: modeLabel },
    { label: 'Total', value: result.total },
  ];

  if (typeof result.changed === 'boolean') {
    rows.push({
      label: 'Gravação',
      value: result.changed ? 'Tabela atualizada' : 'Conteúdo já estava gravado',
    });
  }

  if (result.firstPostingDate) {
    rows.push({ label: 'Primeira data', value: result.firstPostingDate });
  }
  if (result.lastPostingDate) {
    rows.push({ label: 'Última data', value: result.lastPostingDate });
  }
  if (typeof result.history?.total === 'number') {
    rows.push({ label: 'Histórico restante', value: result.history.total });
  }
  if (result.history?.firstPostingDate) {
    rows.push({ label: 'Início do histórico', value: result.history.firstPostingDate });
  }
  if (result.history?.lastPostingDate) {
    rows.push({ label: 'Fim do histórico', value: result.history.lastPostingDate });
  }
  if (typeof result.finishedProducts === 'number') {
    rows.push({ label: 'Produtos acabados', value: result.finishedProducts });
  }
  if (typeof result.products === 'number') {
    rows.push({ label: 'Produtos', value: result.products });
  }
  if (typeof result.eligible === 'number') {
    rows.push({ label: 'Elegíveis para revalidação', value: result.eligible });
  }
  if (typeof result.duplicates === 'number') {
    rows.push({ label: 'Duplicados', value: result.duplicates });
  }
  if (typeof result.skipped === 'number') {
    rows.push({ label: 'Ignorados', value: result.skipped });
  }
  if (typeof result.invalidDates === 'number') {
    rows.push({ label: 'Datas inválidas', value: result.invalidDates });
  }
  if (cycleSheets) {
    rows.push({ label: 'Abas de ciclo', value: cycleSheets });
  }
  if (ignoredSheets) {
    rows.push({ label: 'Abas ignoradas', value: ignoredSheets });
  }

  return (
    <Box className="w-full mt-4">
      <h2 className="font-GilroySemibold text-gray-600 mb-4">
        Resultado da carga
      </h2>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-2">
            <dt className="text-slate-500">{row.label}</dt>
            <dd className="text-slate-800">{row.value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-4">
        <CountList
          title="Por classificação"
          counts={result.byClassification}
          labelFor={(key) => CLASSIFICATION_LABEL[key] || key}
        />
        <CountList title="Por classe" counts={result.byClass} />
        <CountList title="Por ciclo" counts={result.byCycle} />
      </div>
    </Box>
  );
};

export const uploadTypeName = (uploadType: string) => {
  return INVENTORY_UPLOAD_TYPE_OPTIONS.find((option) => option.value === uploadType)?.name
    || uploadType;
};
